import { useEffect, useMemo } from 'react'
import {
    NyuDataPoints,
    NyuDataPointsWithParsedTime,
    NyuDataProperties,
} from '../../../shared/types'
import { getStartEndYearOptionsFromYears } from '../utils/helpers'
import { useGlobalSubState } from './useGlobalSubState'

// Resolves a configured default year (`dataProperties.defaultStartYear` /
// `defaultEndYear`) against the years actually offered by the dropdown.
// Out-of-range values are clamped, so a default that outlives its dataset
// still yields a usable year instead of being silently dropped.
function resolveDefaultYear(
    defaultYear: number | undefined,
    options: string[]
): string | undefined {
    if (!defaultYear || options.length === 0) {
        return undefined
    }

    const first = Number(options[0])
    const last = Number(options[options.length - 1])

    return String(Math.min(Math.max(defaultYear, first), last))
}

function useKeepFallbackYear(
    dataPoints: NyuDataPoints,
    fallbackYear: string,
    key: 'startYear' | 'endYear',
    allYears: number[]
): [string, (value: string) => void] {
    const [year, setYear] = useGlobalSubState(key)

    const yearIsValid = allYears.includes(Number(year))

    useEffect(() => {
        if (!year || !yearIsValid) {
            setYear(fallbackYear)
        }
    }, [yearIsValid])

    const [yearIsFallbackYear, setYearIsFallbackYear] = useGlobalSubState(
        key === 'startYear' ? 'startYearIsEdgeYear' : 'endYearIsEdgeYear'
    )

    useEffect(() => {
        setYearIsFallbackYear(year === fallbackYear)
    }, [year, dataPoints])

    useEffect(() => {
        if (yearIsFallbackYear) {
            setYear(fallbackYear)
        }
    }, [dataPoints])

    const actualYear = yearIsFallbackYear ? fallbackYear : year
    return [actualYear, setYear]
}

export default function useFilterYears(
    dataPoints: NyuDataPointsWithParsedTime,
    dataProperties?: NyuDataProperties
) {
    const allYears = useMemo(() => {
        return dataPoints.map(({ year }) => year)
    }, [dataPoints])

    const { startOptions, endOptions } = useMemo(
        () => getStartEndYearOptionsFromYears(allYears),
        [allYears]
    )
    const firstStartYear = startOptions[0]
    const lastEndYear = endOptions[endOptions.length - 1]

    const startYearFallback =
        resolveDefaultYear(dataProperties?.defaultStartYear, startOptions) ||
        firstStartYear

    const defaultEndYear = resolveDefaultYear(
        dataProperties?.defaultEndYear,
        endOptions
    )
    // an end year that is not after the start year would leave the chart empty,
    // so an inconsistent configuration falls back to the last year of the data
    const endYearFallback =
        defaultEndYear && Number(defaultEndYear) > Number(startYearFallback)
            ? defaultEndYear
            : lastEndYear

    const [startYear, setStartYear] = useKeepFallbackYear(
        dataPoints,
        startYearFallback,
        'startYear',
        allYears
    )

    const [endYear, setEndYear] = useKeepFallbackYear(
        dataPoints,
        endYearFallback,
        'endYear',
        allYears
    )

    const handleSetStartYear = (year: string) => {
        setStartYear(year)
        const validEndYear = Number(year) + 1
        if (Number(endYear) < validEndYear) {
            setEndYear(String(validEndYear))
        }
    }

    const handleSetEndYear = (year: string) => {
        setEndYear(year)
        const validStartYear = Number(year) - 1
        if (Number(startYear) > validStartYear) {
            setStartYear(String(validStartYear))
        }
    }

    const filteredDataPoints = useMemo(() => {
        const startYearNum = Number(startYear)
        const endYearNum = Number(endYear)
        const filteredDataPoints = dataPoints.filter(
            ({ year }: { year: number }) =>
                year >= startYearNum && year <= endYearNum
        )
        return filteredDataPoints
    }, [dataPoints, startYear, endYear])

    return useMemo(
        () => ({
            dataPoints: filteredDataPoints,
            startYearDropdownProps: {
                value: startYear,
                options: startOptions,
                setValue: handleSetStartYear,
            },
            endYearDropdownProps: {
                value: endYear,
                options: endOptions,
                setValue: handleSetEndYear,
            },
        }),
        [filteredDataPoints, startYear, endYear, startOptions, endOptions]
    )
}
